import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './SimpleImageCropper.scss';

interface SimpleImageCropperProps {
  imageSrc: string;
  onCropComplete: (croppedImage: string) => void;
  onCancel: () => void;
}

const SimpleImageCropper: React.FC<SimpleImageCropperProps> = ({
  imageSrc,
  onCropComplete,
  onCancel,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);

  const getCroppedImg = useCallback(() => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return canvas.toDataURL('image/jpeg', 0.9);
  }, [completedCrop]);

  const handleDone = () => {
    const croppedImage = getCroppedImg();
    if (croppedImage) {
      onCropComplete(croppedImage);
    }
  };

  return (
    <div className="simple-cropper-overlay">
      <div className="simple-cropper-modal">
        <div className="simple-cropper-header">
          <h3>Crop Profile Image</h3>
          <button className="simple-cropper-close" onClick={onCancel}>
            ×
          </button>
        </div>
        
        <div className="simple-cropper-body">
          <ReactCrop
            crop={crop}
            onChange={(c: Crop) => setCrop(c)}
            onComplete={(c: PixelCrop) => setCompletedCrop(c)}
            aspect={1} // Square crop for profile image
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              className="simple-cropper-image"
            />
          </ReactCrop>
        </div>

        <div className="simple-cropper-footer">
          <button className="simple-cropper-btn simple-cropper-btn--cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="simple-cropper-btn simple-cropper-btn--primary" onClick={handleDone}>
            Crop & Use Image
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleImageCropper;

